import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Search, Checkbox, Avatar, Text, Divider } from '@vibe/core';
import { Team as TeamIcon, Person, Close } from '@vibe/icons';
import { format } from 'date-fns';
import { fetchBoardGroups, fetchBoardItemsPage } from '../../api/monday.api';
import { getFieldMappingLocal, getSubitemFieldMappingLocal, getItemLevelLocal, getBoardColumns, getSubitemsBoardColumns } from '../../api/board-config.api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserOption {
  id: number;
  name: string;
  avatarUrl?: string;
}

interface TeamOption {
  id: number;
  name: string;
  memberNames?: string[];
}

interface GroupOption {
  id: string;
  name: string;
}

interface BoardOption {
  id: number;
  name: string;
}

/** An assignee entry can be either a person or a team */
export interface AssigneeEntry {
  id: number;
  kind: 'person' | 'team';
}

export interface CreateItemDialogProps {
  boardId: number;
  /** Pre-filled start date from cell click or range selection */
  initialDate?: Date;
  /** Pre-filled end date from range selection */
  initialEndDate?: Date;
  /** Pre-filled assignee from resource row click */
  initialAssigneeId?: number;
  /** Whether the pre-filled assignee is a person or team */
  initialAssigneeKind?: 'person' | 'team';
  /** Available users */
  assigneeOptions: UserOption[];
  /** Available teams */
  teamOptions: TeamOption[];
  /** Available groups (used when a single board is active) */
  groupOptions: GroupOption[];
  /** When in workspace multi-board mode, the list of boards to choose from */
  multiBoardOptions?: BoardOption[];
  /** Board item level configuration (main/sub/both) */
  itemLevel?: 'main' | 'sub' | 'both';
  /** Close the dialog */
  onClose: () => void;
  /** Create a new item on monday.com */
  onCreateItem: (data: {
    boardId: number;
    name: string;
    groupId: string;
    assignees: AssigneeEntry[];
    startDate: string;
    endDate: string;
    hours: number;
    hoursPerDay?: number;
    hoursTotal?: number;
    status?: string;
    /** When set, create a subitem under this parent item */
    parentItemId?: number;
  }) => void;
  /** Open the shared leave request dialog */
  onOpenLeaveDialog?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const CreateItemDialog: React.FC<CreateItemDialogProps> = ({
  boardId,
  initialDate,
  initialEndDate,
  initialAssigneeId,
  initialAssigneeKind = 'person',
  assigneeOptions,
  teamOptions,
  groupOptions,
  multiBoardOptions,
  itemLevel = 'main',
  onClose,
  onCreateItem,
  onOpenLeaveDialog,
}) => {
  const isMultiBoard = multiBoardOptions != null && multiBoardOptions.length > 1;

  // ── Board & group state ──────────────────────────────────────────────
  const [selectedBoardId, setSelectedBoardId] = useState(boardId);

  // ── Item level — re-evaluate when board changes in multi-board mode ──
  const effectiveItemLevel = isMultiBoard ? getItemLevelLocal(selectedBoardId) : (itemLevel ?? 'main');
  const canCreateSubitems = effectiveItemLevel === 'sub' || effectiveItemLevel === 'both';
  const canCreateMainItems = effectiveItemLevel === 'main' || effectiveItemLevel === 'both';

  // ── Subitem mode toggle ────────────────────────────────────────────
  const [isSubitemMode, setIsSubitemMode] = useState(effectiveItemLevel === 'sub');
  const [parentItemSearch, setParentItemSearch] = useState('');
  const [parentItems, setParentItems] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);
  const [parentItemsLoading, setParentItemsLoading] = useState(false);

  // Reset subitem mode when board changes and item level changes
  useEffect(() => {
    if (effectiveItemLevel === 'sub') {
      setIsSubitemMode(true);
    } else if (effectiveItemLevel === 'main') {
      setIsSubitemMode(false);
    }
    setSelectedParentId(null);
    setParentItemSearch('');
  }, [effectiveItemLevel]);
  const [dynamicGroups, setDynamicGroups] = useState<GroupOption[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // Effective groups: in multi-board mode, use dynamically loaded groups; otherwise use prop
  const effectiveGroups = isMultiBoard ? dynamicGroups : groupOptions;

  // Load groups when board selection changes in multi-board mode
  useEffect(() => {
    if (!isMultiBoard) return;
    setGroupsLoading(true);
    setDynamicGroups([]);
    fetchBoardGroups(selectedBoardId)
      .then((groups) => {
        const mapped = groups.map((g) => ({ id: g.id, name: g.title }));
        setDynamicGroups(mapped);
        setGroupId(mapped[0]?.id ?? '');
      })
      .catch(() => {
        setDynamicGroups([]);
        setGroupId('');
      })
      .finally(() => setGroupsLoading(false));
  }, [isMultiBoard, selectedBoardId]);

  // Load parent items for subitem mode (debounced search)
  useEffect(() => {
    if (!isSubitemMode) return;
    const timer = setTimeout(async () => {
      setParentItemsLoading(true);
      try {
        const result = await fetchBoardItemsPage(selectedBoardId, {
          limit: 50,
          includeSubitems: false,
          filters: { searchText: parentItemSearch || undefined },
        });
        setParentItems(result.items.map((i) => ({ id: Number(i.id), name: i.name })));
      } catch {
        setParentItems([]);
      } finally {
        setParentItemsLoading(false);
      }
    }, parentItemSearch ? 300 : 0);
    return () => clearTimeout(timer);
  }, [isSubitemMode, selectedBoardId, parentItemSearch]);

  // ── Create Item state ────────────────────────────────────────────────
  const [itemName, setItemName] = useState('');
  const [groupId, setGroupId] = useState(groupOptions[0]?.id ?? '');
  const [selectedAssignees, setSelectedAssignees] = useState<AssigneeEntry[]>(
    initialAssigneeId ? [{ id: initialAssigneeId, kind: initialAssigneeKind }] : [],
  );
  const [startDate, setStartDate] = useState(
    initialDate ? format(initialDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
  );
  const [endDate, setEndDate] = useState(
    initialEndDate
      ? format(initialEndDate, 'yyyy-MM-dd')
      : (initialDate ? format(initialDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')),
  );
  const [hoursPerDay, setHoursPerDay] = useState('8');
  const [hoursTotal, setHoursTotal] = useState('0');
  const [status, setStatus] = useState('');

  // ── Field visibility based on field mapping ─────────────────────────
  const hoursConfig = useMemo(() => {
    const fm = isSubitemMode
      ? getSubitemFieldMappingLocal(selectedBoardId)
      : getFieldMappingLocal(selectedBoardId);
    const hasPerDay = !!fm?.scheduledHoursPerDayColumnId;
    const hasTotal = !!fm?.scheduledHoursColumnId;
    return { hasPerDay, hasTotal, hasBoth: hasPerDay && hasTotal };
  }, [selectedBoardId, isSubitemMode]);

  // Cache of column id → type for the target board (or subitem board).
  // Used to validate that mapped "status" column actually has type 'status';
  // a mismatch (e.g. status id pointing at a numeric column) would otherwise
  // silently render the Status dropdown and fail creation on submit.
  const [columnTypesByBoard, setColumnTypesByBoard] = useState<Record<string, Record<string, string>>>({});

  useEffect(() => {
    if (!selectedBoardId) return;
    const cacheKey = `${isSubitemMode ? 'sub' : 'main'}:${selectedBoardId}`;
    if (columnTypesByBoard[cacheKey]) return;
    let cancelled = false;
    (async () => {
      try {
        const cols = isSubitemMode
          ? (await getSubitemsBoardColumns(selectedBoardId)).columns
          : await getBoardColumns(selectedBoardId);
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const c of cols) map[c.id] = c.type;
        setColumnTypesByBoard((prev) => ({ ...prev, [cacheKey]: map }));
      } catch {
        // Non-fatal — we simply fall back to hiding the status dropdown.
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBoardId, isSubitemMode, columnTypesByBoard]);

  const hasStatusColumn = useMemo(() => {
    const fm = isSubitemMode
      ? getSubitemFieldMappingLocal(selectedBoardId)
      : getFieldMappingLocal(selectedBoardId);
    if (!fm?.statusColumnId) return false;
    const cacheKey = `${isSubitemMode ? 'sub' : 'main'}:${selectedBoardId}`;
    const types = columnTypesByBoard[cacheKey];
    // If types haven't loaded yet, hide rather than show a potentially broken
    // dropdown — the worst case is a moment of missing UI, not a failed submit.
    if (!types) return false;
    return types[fm.statusColumnId] === 'status';
  }, [selectedBoardId, isSubitemMode, columnTypesByBoard]);

  // ── Assignee picker state ──────────────────────────────────────────
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);

  const lowerSearch = searchValue.toLowerCase();

  const filteredUsers = useMemo(
    () => assigneeOptions.filter((u) => u.name.toLowerCase().includes(lowerSearch)),
    [assigneeOptions, lowerSearch],
  );

  const filteredTeams = useMemo(
    () => teamOptions.filter((t) => t.name.toLowerCase().includes(lowerSearch)),
    [teamOptions, lowerSearch],
  );

  const isSelected = useCallback(
    (id: number, kind: 'person' | 'team') =>
      selectedAssignees.some((a) => a.id === id && a.kind === kind),
    [selectedAssignees],
  );

  const toggleAssignee = useCallback(
    (id: number, kind: 'person' | 'team') => {
      setSelectedAssignees((prev) => {
        const exists = prev.some((a) => a.id === id && a.kind === kind);
        if (exists) return prev.filter((a) => !(a.id === id && a.kind === kind));
        return [...prev, { id, kind }];
      });
    },
    [],
  );

  const removeAssignee = useCallback(
    (id: number, kind: 'person' | 'team') => {
      setSelectedAssignees((prev) => prev.filter((a) => !(a.id === id && a.kind === kind)));
    },
    [],
  );

  /** Resolve display name for a selected assignee chip */
  const getAssigneeName = useCallback(
    (entry: AssigneeEntry) => {
      if (entry.kind === 'person') {
        return assigneeOptions.find((u) => u.id === entry.id)?.name ?? `User ${entry.id}`;
      }
      return teamOptions.find((t) => t.id === entry.id)?.name ?? `Team ${entry.id}`;
    },
    [assigneeOptions, teamOptions],
  );

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleCreateItem = () => {
    if (!itemName.trim()) return;
    if (isSubitemMode && !selectedParentId) return;
    onCreateItem({
      boardId: selectedBoardId,
      name: itemName.trim(),
      groupId,
      assignees: selectedAssignees,
      startDate,
      endDate,
      hoursPerDay: hoursConfig.hasPerDay ? (Number(hoursPerDay) || 0) : undefined,
      hoursTotal: hoursConfig.hasTotal ? (Number(hoursTotal) || 0) : undefined,
      hours: Number(hoursPerDay) || 0,
      status: status || undefined,
      parentItemId: isSubitemMode && selectedParentId ? selectedParentId : undefined,
    });
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Close picker when clicking outside
  const handlePickerBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    // Check if the new focus target is still within the picker
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setPickerOpen(false);
      setSearchValue('');
    }
  }, []);

  return (
    <div className="rp-create-dialog-overlay" onClick={handleOverlayClick}>
      <div className="rp-create-dialog">
        {/* Header */}
        <div className="rp-create-dialog__header">
          <h3>{isSubitemMode ? 'New Subitem' : 'New Item'}</h3>
          <button className="rp-create-dialog__close" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Item/Subitem toggle (only when both modes are available) */}
        {canCreateSubitems && canCreateMainItems && (
          <div className="rp-create-dialog__tabs">
            <button
              className={`rp-create-dialog__tab ${!isSubitemMode ? 'rp-create-dialog__tab--active' : ''}`}
              onClick={() => setIsSubitemMode(false)}
            >
              Item
            </button>
            <button
              className={`rp-create-dialog__tab ${isSubitemMode ? 'rp-create-dialog__tab--active' : ''}`}
              onClick={() => setIsSubitemMode(true)}
            >
              Subitem
            </button>
          </div>
        )}

        {/* Body */}
        <div className="rp-create-dialog__body">
          {/* Board (only shown in multi-board workspace mode) */}
          {isMultiBoard && (
            <div className="rp-create-dialog__field">
              <label>Board</label>
              <select
                value={selectedBoardId}
                onChange={(e) => setSelectedBoardId(Number(e.target.value))}
              >
                {multiBoardOptions!.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Parent item picker (subitem mode only) */}
          {isSubitemMode && (
            <div className="rp-create-dialog__field">
              <label>Parent Item</label>
              <input
                type="text"
                placeholder="Search parent item..."
                value={parentItemSearch}
                onChange={(e) => {
                  setParentItemSearch(e.target.value);
                  setSelectedParentId(null);
                }}
              />
              {(parentItemsLoading || parentItems.length > 0) && !selectedParentId && (
                <div className="rp-create-dialog__parent-list">
                  {parentItemsLoading && <div className="rp-create-dialog__parent-loading">Loading...</div>}
                  {!parentItemsLoading && parentItems.map((item) => (
                    <div
                      key={item.id}
                      className="rp-create-dialog__parent-option"
                      onClick={() => {
                        setSelectedParentId(item.id);
                        setParentItemSearch(item.name);
                      }}
                    >
                      {item.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Group (hidden in subitem mode — subitems inherit parent's group) */}
          {!isSubitemMode && (
          <div className="rp-create-dialog__field">
            <label>Group</label>
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              disabled={isMultiBoard && groupsLoading}
            >
              {groupsLoading && <option value="">Loading groups...</option>}
              {!groupsLoading && effectiveGroups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
              {!groupsLoading && effectiveGroups.length === 0 && <option value="">No groups</option>}
            </select>
          </div>
          )}

          {/* Item Name */}
          <div className="rp-create-dialog__field">
            <label>Item Name</label>
            <input
              type="text"
              placeholder="Enter item name..."
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              autoFocus
            />
          </div>

          {/* Assignees – multi-select picker */}
          <div className="rp-create-dialog__field">
            <label>Assignees</label>
            <div
              className="rp-assignee-picker"
              ref={pickerRef}
              onBlur={handlePickerBlur}
              tabIndex={-1}
            >
              {/* Trigger / selected chips */}
              <div
                className="rp-assignee-picker__trigger"
                onClick={() => { setPickerOpen(true); }}
              >
                {selectedAssignees.length === 0 && (
                  <span className="rp-assignee-picker__placeholder">
                    Select users or teams...
                  </span>
                )}
                {selectedAssignees.map((entry) => (
                  <span
                    key={`${entry.kind}-${entry.id}`}
                    className="rp-assignee-picker__chip"
                  >
                    {entry.kind === 'team' ? (
                      <TeamIcon className="rp-assignee-picker__chip-icon" />
                    ) : (
                      <Person className="rp-assignee-picker__chip-icon" />
                    )}
                    <span className="rp-assignee-picker__chip-label">
                      {getAssigneeName(entry)}
                    </span>
                    <button
                      className="rp-assignee-picker__chip-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeAssignee(entry.id, entry.kind);
                      }}
                      type="button"
                    >
                      <Close />
                    </button>
                  </span>
                ))}
              </div>

              {/* Dropdown panel */}
              {pickerOpen && (
                <div className="rp-assignee-picker__dropdown">
                  <div className="rp-assignee-picker__search">
                    <Search
                      size="small"
                      value={searchValue}
                      onChange={(value: string) => setSearchValue(value)}
                      placeholder="Search person or team..."
                      autoFocus
                    />
                  </div>

                  <Divider />

                  <div className="rp-assignee-picker__list">
                    {/* Users section */}
                    {filteredUsers.length > 0 && (
                      <>
                        <Text
                          type={Text.types.TEXT2}
                          className="rp-assignee-picker__section-title"
                        >
                          Users
                        </Text>
                        {filteredUsers.map((user) => (
                          <div
                            key={`user-${user.id}`}
                            className="rp-assignee-picker__option"
                            onClick={() => toggleAssignee(user.id, 'person')}
                          >
                            <Checkbox
                              checked={isSelected(user.id, 'person')}
                              onChange={() => toggleAssignee(user.id, 'person')}
                            />
                            {user.avatarUrl ? (
                              <Avatar
                                type="img"
                                src={user.avatarUrl}
                                size="small"
                                ariaLabel={user.name}
                              />
                            ) : (
                              <Avatar
                                type="text"
                                text={user.name}
                                size="small"
                                ariaLabel={user.name}
                              />
                            )}
                            <Text type={Text.types.TEXT2}>{user.name}</Text>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Teams section */}
                    {filteredTeams.length > 0 && (
                      <>
                        <Text
                          type={Text.types.TEXT2}
                          className="rp-assignee-picker__section-title"
                        >
                          Teams
                        </Text>
                        {filteredTeams.map((team) => (
                          <div
                            key={`team-${team.id}`}
                            className="rp-assignee-picker__option"
                            onClick={() => toggleAssignee(team.id, 'team')}
                          >
                            <Checkbox
                              checked={isSelected(team.id, 'team')}
                              onChange={() => toggleAssignee(team.id, 'team')}
                            />
                            <Avatar
                              type="icon"
                              icon={TeamIcon}
                              size="small"
                              ariaLabel={team.name}
                            />
                            <div className="rp-assignee-picker__option-info">
                              <Text type={Text.types.TEXT2}>{team.name}</Text>
                              {team.memberNames && team.memberNames.length > 0 && (
                                <Text
                                  type={Text.types.TEXT2}
                                  className="rp-assignee-picker__option-desc"
                                >
                                  {team.memberNames.join(', ')}
                                </Text>
                              )}
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    {filteredUsers.length === 0 && filteredTeams.length === 0 && (
                      <Text
                        type={Text.types.TEXT2}
                        className="rp-assignee-picker__empty"
                      >
                        No results found
                      </Text>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="rp-create-dialog__field">
            <label>Timeline</label>
            <div className="rp-create-dialog__row">
              <div className="rp-create-dialog__field">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="rp-create-dialog__field">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Hours — side by side when both are present */}
          {(hoursConfig.hasPerDay || hoursConfig.hasTotal) && (
            <div style={{ display: 'flex', gap: '12px' }}>
              {hoursConfig.hasPerDay && (
                <div className="rp-create-dialog__field" style={{ flex: 1 }}>
                  <label>Hours per Day</label>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={hoursPerDay}
                    onChange={(e) => setHoursPerDay(e.target.value)}
                  />
                </div>
              )}
              {hoursConfig.hasTotal && (
                <div className="rp-create-dialog__field" style={{ flex: 1 }}>
                  <label>Hours (Total)</label>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={hoursTotal}
                    onChange={(e) => setHoursTotal(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}


          {/* Status (only when status column is mapped) */}
          {hasStatusColumn && (
            <div className="rp-create-dialog__field">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Select a status</option>
                <option value="Working on it">Working on it</option>
                <option value="Stuck">Stuck</option>
                <option value="Done">Done</option>
              </select>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="rp-create-dialog__footer">
          <div className="rp-create-dialog__footer-left">
            {onOpenLeaveDialog && (
              <Button
                kind={Button.kinds.SECONDARY}
                size={Button.sizes.SMALL}
                onClick={onOpenLeaveDialog}
              >
                Request Leave
              </Button>
            )}
          </div>
          <div className="rp-create-dialog__footer-right">
            <Button
              kind={Button.kinds.TERTIARY}
              size={Button.sizes.SMALL}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              size={Button.sizes.SMALL}
              onClick={handleCreateItem}
              disabled={!itemName.trim() || (!isSubitemMode && !groupId) || (isSubitemMode && !selectedParentId)}
            >
              Create Item
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateItemDialog;
